/**
 * ESG export — three-file per-scenario export + auto-generated methodology
 * sheet (SIL-438, W3 of resources/plans/sustainability-forecasting-spec.md
 * §8, copy locked in resources/plans/sil-426-carbon-disclosure-copy.md
 * §14-15).
 *
 * Pure builders only — no DB/IO. Consumes the SIL-435 (W2) carbon kernel
 * (`use-phase/scenario-carbon-kernel.ts`) exactly as published: this module
 * NEVER reimplements `buildCarbonScenarioSeries`'s arithmetic, and it never
 * imports `AdvisoryKgCo2e` or any advisory function — the three export files
 * are the OFFICIAL footprint only (spec's two-panel firewall), so the
 * advisory panel simply has no seam into this module at all.
 *
 * ── VERBATIM COPY ──────────────────────────────────────────────────────────
 * Every `ESG_*` string constant below is copied byte-for-byte (including the
 * em dash `—` and curly apostrophe `'` where the source file uses them) from
 * sil-426-carbon-disclosure-copy.md — never paraphrased. A few §14 items
 * (the exclusion-justification fragments for storage/switches, and the
 * per-input-class data-quality statements) have NO locked customer-facing
 * sentence in that file — §14 only lists them as terse fragments the sheet
 * must cover ("storage — transfer policy", "switches — no embodied data",
 * "a data-quality statement per input class (measured / derived / platform
 * default)"). Those few are composed sentences grounded in the spec's own
 * wording and flagged inline as INTERPRETED, not verbatim customer copy.
 *
 * ── REPORTING-YEAR AGGREGATION (LOAD-BEARING) ────────────────────────────
 * The kernel's `CarbonScenarioSeriesPoint[]` is CUMULATIVE-TO-DATE per month
 * (module header of scenario-carbon-kernel.ts). The export is per REPORTING
 * YEAR, so `aggregateCarbonSeriesByReportingYear` below DE-CUMULATES by
 * differencing the point at each year boundary against the prior year
 * boundary — summing the monthly points directly (or reading raw
 * cumulative values as if they were annual) would double- (triple-,
 * quadruple-...) count every earlier year's contribution. See this file's
 * test suite for a pinned regression proving the naive approach is wrong.
 */

import {
  buildCarbonScenarioSeries,
  subtractCategoryPoints,
  ZERO_CATEGORY_POINT,
  type CarbonScenarioCohort,
  type CarbonScenarioSeriesPoint,
  type CategoryKgCo2ePoint,
  type GhgCategoryKey,
  type CarbonKernelBasis,
} from '../use-phase/scenario-carbon-kernel.js';
import { USE_PHASE_NO_MARKET_BASED_STATEMENT } from './use-phase.js';
import { csvLine } from '../csv.js';
import { CARBON_TARGET_BASIS, type CarbonTargetDto } from './sustainability.js';

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

// ---------------------------------------------------------------------------
// 1. Verbatim copy constants (sil-426-carbon-disclosure-copy.md)
// ---------------------------------------------------------------------------

/** §1 — global framing, every carbon surface AND export header (verbatim,
 * em dash preserved). Rendered as a single FORCE-QUOTED CSV cell in every
 * export file's header — see `esgFramingLineRow`. */
export const ESG_FRAMING_LINE = 'Planning forecast — not a GHG inventory.';

/** §14 — the full verification caveat (verbatim; §1's short form is what
 * every other surface renders, this is the only place the long form lives). */
export const ESG_VERIFICATION_CAVEAT =
  'Figures are scenario projections for decision support and are not suitable for regulatory or ' +
  'voluntary GHG reporting without independent verification.';

/** §2 — location-based method badge (verbatim). */
export const ESG_LOCATION_BASED_METHOD_BADGE = 'Location-based method';

/** §2 — tooltip/footnote (verbatim). */
export const ESG_LOCATION_BASED_METHOD_NOTE =
  'Operational emissions use location-based grid-average factors (source and version in the ' +
  'methodology sheet). Market-based Scope 2 (your contractual instruments, RECs, PPAs) is not ' +
  'modeled. A GHG inventory under the Scope 2 Guidance would report both methods.';

/** §3 — constant-grid-intensity caveat (verbatim); also doubles as the §14
 * "grid decarbonization" exclusion's justification text (see
 * `ESG_EXCLUSIONS` below). */
export const ESG_CONSTANT_GRID_INTENSITY_CAVEAT =
  'Grid intensity is held constant over the horizon; actual grids are expected to decarbonize, so ' +
  'later-year operational figures are likely overstated.';

/** §4 — the five footprint category labels (verbatim), keyed by the kernel's
 * own `GhgCategoryKey` so a caller can never desync a label from its data
 * column. */
export const ESG_CATEGORY_LABELS: Record<GhgCategoryKey, string> = {
  scope2: 'Scope 2 · electricity (operated facilities)',
  cat8: 'Category 8 · electricity (colocation)',
  cat1_or_2: 'Category 1/2 · purchased hardware (embodied)',
  cat5: 'Category 5 · end-of-life disposal',
  cat4: 'Category 4 · transport (moves and used purchases)',
};

/** §4 — Category 8 (colocation) helper (verbatim) — the Cat-8 colocation
 * convention note the methodology sheet must carry (§14). */
export const ESG_CAT8_COLO_CONVENTION_NOTE =
  'Colocation electricity is assigned to Category 8 (upstream leased assets) by platform ' +
  'convention; depending on your lease terms and consolidation approach it may belong in Scope 2. ' +
  'Disclosed in the methodology sheet.';

/** §4 — Category 1/2 helper (verbatim). */
export const ESG_CAT1_OR_2_HELPER =
  "Categorized per your organization's fixed-asset treatment (set in Financials setup).";

/** §4 — Category 5 timing helper (verbatim). */
export const ESG_CAT5_TIMING_HELPER =
  'Expected future emissions from disposal actions taken in the month shown.';

/** §5 — unlocated assets (verbatim). */
export const ESG_UNLOCATED_ASSETS_NOTE =
  'Assets without a location use the world-average grid factor and are assumed to run in operated ' +
  '(non-colocation) facilities.';

/** §6 — sell action note / Cat 11-12 exclusion (verbatim). Doubles as the
 * §14 "Cat 11/12" exclusion's justification text — the copy file's own
 * parenthetical says this is what "carries the justification text itself,
 * not just the list" in the methodology sheet. */
export const ESG_SELL_CAT_11_12_EXCLUSION_NOTE =
  "Selling ends these emissions in your forecast from the sale month. The hardware's future " +
  'use-phase and end-of-life emissions transfer to its next owner — they do not disappear. ' +
  'Downstream categories (11 and 12) are not modeled in this forecast. This exclusion and its ' +
  'rationale are listed in the methodology sheet, consistent with the GHG Protocol Scope 3 ' +
  "Standard's practice of disclosing excluded categories with justification.";

/** §7 — buy-used embodied convention (verbatim). */
export const ESG_BUY_USED_EMBODIED_CONVENTION =
  'Used purchases inherit ~0 embodied carbon in this model, plus a transport and refurbishment ' +
  'adder. This is a disclosed modeling convention of this platform — the GHG Protocol does not ' +
  'prescribe an embodied-carbon treatment for second-hand goods. This convention may understate ' +
  'Category 1/2 emissions relative to conventions that allocate a share of original manufacturing.';

/** §8 — buy-new embodied (verbatim). */
export const ESG_BUY_NEW_EMBODIED_NOTE =
  "New purchases add the SKU's full embodied carbon in the purchase month, one time, never spread " +
  'over the years of use — consistent with GHG Protocol Scope 3 guidance to account for acquisition ' +
  'emissions in the year of purchase, not amortized (Scope 3 Standard §5.4, Box 5.3). Month-level ' +
  'attribution is a platform convention.';

/** §9 — disposal adder basis (verbatim). */
export const ESG_DISPOSAL_ADDER_BASIS_NOTE =
  'Disposal emissions cover collection and transport to the recycling facility only, consistent ' +
  'with the GHG Protocol recycling guidance (Scope 3 Standard, Box 5.6). The recycling process ' +
  'itself is attributed to the user of the recovered material. Default factor: UK BEIS/Defra WEEE ' +
  '(mixed, open-loop) 21.28 kgCO2e/tonne × unit weight (factor version in methodology sheet); ' +
  'overridable.';

/**
 * §13 — move action note (verbatim, per SIL-428). A TEMPLATE, not a
 * constant: the locked string carries three interpolation slots
 * (`{source}`, `{target}`, `{m}`) and is only meaningful once a specific
 * relocation is described, so it ships as the function that fills them
 * rather than as a raw string a caller could render with the braces still
 * showing. Wording outside the slots is byte-for-byte the copy file's.
 */
export function esgMoveActionNote(params: {
  /** Source location as shown to the reader — a Boavizta trigram, or the
   * platform's own label for an asset with no resolved location. */
  source: string;
  /** Target location as shown to the reader (never the WOR no-location
   * floor — moves to "No location" are disallowed, spec §3). */
  target: string;
  /** Whole-month action timing (`m` in the copy). */
  month: number;
}): string {
  return (
    `Relocating switches this cohort's grid factor from ${params.source} to ${params.target} ` +
    `beginning month ${params.month}, and adds a one-time freight transport estimate ` +
    '(Category 4). Relocation does not change embodied carbon.'
  );
}

/** §12 — target settings helper (verbatim). */
export const ESG_TARGET_SETTINGS_HELPER =
  'Absolute annual budget, drawn flat across the horizon. Type (absolute) and Scope 2 method basis ' +
  '(location-based) are recorded automatically in the methodology sheet. Base year is optional ' +
  'here; if you set one, note the GHG Protocol requires an accompanying recalculation policy — ' +
  'both are included in disclosures when provided.';

/** §12 — location/team targets helper (verbatim) — also the source for
 * spec §4/§8's "ESG export carries the organization target only". */
export const ESG_TARGET_LENS_HELPER =
  'Location and team targets are planning aids. They overlap (an asset belongs to both) and are ' +
  'never summed with each other or the organization target. The ESG export carries the ' +
  'organization target only.';

/** §14 — "Biogenic CO2: not applicable" (verbatim). */
export const ESG_BIOGENIC_NOTE = 'Biogenic CO2: not applicable';

/** §14 — timing convention fragment, rendered as a full sentence (verbatim
 * fragment: "timing convention (whole-month, operate through m-1, act at
 * m)"). */
export const ESG_TIMING_CONVENTION_NOTE =
  'Timing convention: whole-month — a cohort operates through month m-1, and its action (lump, ' +
  'buy start, move switch) lands at month m.';

/** §14 — "GWP value set/source (as inherited from the factor sources)"
 * fragment. INTERPRETED beyond the bracketed fragment: the two named
 * sources are grounded in code (CARBON_EMBODIED_EXPLAINER's BoaviztAPI
 * citation, dto/carbon.ts; SIL-420's UK BEIS/Defra citation, this file's
 * `ESG_DISPOSAL_ADDER_BASIS_NOTE`) — no separate GWP characterization (e.g.
 * an AR5/AR6 citation) is asserted here because none is recorded anywhere
 * in this codebase; disclosing that plainly is safer than inventing one. */
export const ESG_GWP_SOURCE_NOTE =
  'GWP value set/source: as inherited from the factor sources (BoaviztAPI embodied-carbon ' +
  'methodology; UK BEIS/Defra Conversion Factors for the disposal and freight adders). SiliconX ' +
  'does not apply or override a GWP characterization of its own.';

/** §14 — "a data-quality statement per input class (measured / derived /
 * platform default)". INTERPRETED — no locked customer sentence exists in
 * the copy file for these three; composed from the spec's own vocabulary
 * (measured/derived/platform-default rungs already used throughout
 * use-phase/accuracy-tier.ts). */
export const ESG_DATA_QUALITY_STATEMENTS: Record<'measured' | 'derived' | 'platformDefault', string> = {
  measured:
    'Measured: computed from telemetry-observed device power draw for the assets in this cohort.',
  derived:
    'Derived: computed from a resolved wattage estimate × PUE × grid intensity factor, not a ' +
    'direct measurement.',
  platformDefault:
    'Platform default: no asset-specific or organization-specific input was available for this ' +
    'figure, so a platform default value was used.',
};

/** §14 — exclusion list WITH justification text per exclusion. Cat 11/12
 * reuses `ESG_SELL_CAT_11_12_EXCLUSION_NOTE` (verbatim, §6). Market-based
 * Scope 2 reuses `USE_PHASE_NO_MARKET_BASED_STATEMENT` (verbatim,
 * dto/use-phase.ts — never re-authored here). Grid decarbonization reuses
 * `ESG_CONSTANT_GRID_INTENSITY_CAVEAT` (verbatim, §3). Storage and switches
 * have no locked sentence in the copy file — §14 gives only the terse
 * fragments "storage — transfer policy" / "switches — no embodied data";
 * the two sentences below are INTERPRETED from those fragments plus spec
 * §3's "Storage excluded by transfer policy... switches by no-data". */
export const ESG_EXCLUSIONS: ReadonlyArray<{ category: string; justification: string }> = [
  { category: 'Cat 11/12 (downstream use-phase and end-of-life)', justification: ESG_SELL_CAT_11_12_EXCLUSION_NOTE },
  {
    category: 'Storage',
    justification:
      'Storage-class hardware is excluded from the buy picker by platform transfer policy, not a ' +
      'data limitation.',
  },
  {
    category: 'Switches',
    justification:
      'Switch-class hardware is excluded from the buy picker — no embodied-carbon data is ' +
      'available for this class today.',
  },
  { category: 'Market-based Scope 2', justification: USE_PHASE_NO_MARKET_BASED_STATEMENT },
  { category: 'Grid decarbonization', justification: ESG_CONSTANT_GRID_INTENSITY_CAVEAT },
];

// ---------------------------------------------------------------------------
// 2. Framing-line CSV cell — forced quoting, independent of csvCell()'s
//    need-based quoting (csvCell only quotes when a comma/quote/newline is
//    present; the framing line has none, so csvCell alone would NOT quote
//    it). The ticket requires this line survive as ONE cell under a naive
//    CSV parser — force-quoting is the only way to guarantee that
//    regardless of future copy edits that might introduce a comma.
// ---------------------------------------------------------------------------

/** Wraps `raw` in RFC-4180 double quotes unconditionally (doubling any
 * embedded `"`), never relying on csvCell()'s need-based quoting. */
export function forceQuotedCsvCell(raw: string): string {
  return `"${raw.replaceAll('"', '""')}"`;
}

/** The framing-line header row every export file emits FIRST, before its
 * column header row — a single force-quoted cell. */
export function esgFramingLineRow(): string {
  return forceQuotedCsvCell(ESG_FRAMING_LINE) + '\n';
}

// ---------------------------------------------------------------------------
// 3. Reporting-year aggregation (de-cumulation) — see module header.
// ---------------------------------------------------------------------------

export interface EsgReportingYearPoint {
  /** 1-based reporting year. */
  reportingYear: number;
  /** This year's OWN (non-cumulative) contribution — baseline lens. */
  baseline: CategoryKgCo2ePoint;
  /** This year's OWN (non-cumulative) contribution — scenario lens. */
  scenario: CategoryKgCo2ePoint;
}

/**
 * De-cumulates a cumulative-to-date monthly series into per-reporting-year
 * figures: year N's contribution is `series[N*12] - series[(N-1)*12]` for
 * both lenses independently — NEVER a raw sum of the monthly points, which
 * would double-count every prior year's total (see module header).
 *
 * `monthsPerYear` defaults to 12 (calendar-year reporting periods, anchored
 * at the scenario's own month 0 — this module has no calendar-date anchor
 * of its own; see the API layer for how a reporting_year number is
 * presented to the customer). The final year is clipped to the series'
 * own horizon when the horizon is not an exact multiple of `monthsPerYear`
 * (e.g. a 30-month horizon's year 3 covers months 25..30, not 25..36).
 *
 * @pure
 */
export function aggregateCarbonSeriesByReportingYear(
  series: readonly CarbonScenarioSeriesPoint[],
  monthsPerYear = 12,
): EsgReportingYearPoint[] {
  if (series.length === 0) return [];
  const horizonMonths = series[series.length - 1]!.month;
  const byMonth = new Map(series.map((p) => [p.month, p] as const));

  const results: EsgReportingYearPoint[] = [];
  let prevBaseline: CategoryKgCo2ePoint = ZERO_CATEGORY_POINT;
  let prevScenario: CategoryKgCo2ePoint = ZERO_CATEGORY_POINT;

  for (let yearEndMonth = monthsPerYear; ; yearEndMonth += monthsPerYear) {
    const clampedMonth = Math.min(yearEndMonth, horizonMonths);
    const point = byMonth.get(clampedMonth);
    if (!point) break; // malformed series (gap) — stop rather than fabricate a year.

    results.push({
      reportingYear: results.length + 1,
      baseline: subtractCategoryPoints(point.baseline, prevBaseline),
      scenario: subtractCategoryPoints(point.scenario, prevScenario),
    });
    prevBaseline = point.baseline;
    prevScenario = point.scenario;

    if (clampedMonth >= horizonMonths) break;
  }

  return results;
}

// ---------------------------------------------------------------------------
// 4. File 1 — long/tidy CSV: one row per (scenario, lens, reporting_year,
//    ghg_category, cohort).
// ---------------------------------------------------------------------------

export const ESG_TIDY_CSV_COLUMNS = [
  'scenario',
  'cohort',
  'lens',
  'reporting_year',
  'ghg_category',
  'kgco2e',
  'basis_tag',
] as const;

export type EsgLens = 'baseline' | 'scenario';

export interface EsgTidyRow {
  cohortLabel: string;
  reportingYear: number;
  lens: EsgLens;
  ghgCategory: GhgCategoryKey;
  kgCo2e: number;
  basisTag: string;
}

const GHG_CATEGORY_KEYS: readonly GhgCategoryKey[] = ['scope2', 'cat8', 'cat1_or_2', 'cat5', 'cat4'];

/**
 * Builds every tidy row for ONE cohort: runs the kernel over a
 * SINGLE-cohort input (so per-cohort category figures are available — the
 * kernel's own `buildCarbonScenarioSeries` only returns an aggregate across
 * every cohort passed to it), de-cumulates into reporting years, then
 * emits one row per (lens x category x reporting_year) — every combination,
 * not just the non-zero ones, so a downstream pivot never has to guess
 * whether a missing row means "zero" or "not computed."
 *
 * @pure
 */
export function buildEsgTidyRowsForCohort(
  cohortLabel: string,
  cohort: CarbonScenarioCohort,
  horizonMonths: number,
  basisTag: string,
  monthsPerYear = 12,
): EsgTidyRow[] {
  const series = buildCarbonScenarioSeries({ horizonMonths, cohorts: [cohort] });
  const years = aggregateCarbonSeriesByReportingYear(series, monthsPerYear);

  const rows: EsgTidyRow[] = [];
  for (const year of years) {
    for (const lens of ['baseline', 'scenario'] as const) {
      const point = year[lens];
      for (const category of GHG_CATEGORY_KEYS) {
        rows.push({
          cohortLabel,
          reportingYear: year.reportingYear,
          lens,
          ghgCategory: category,
          kgCo2e: point[category],
          basisTag,
        });
      }
    }
  }
  return rows;
}

/** @pure */
export function buildEsgTidyCsv(scenarioLabel: string, rows: readonly EsgTidyRow[]): string {
  const lines: string[] = [esgFramingLineRow(), ESG_TIDY_CSV_COLUMNS.join(',') + '\n'];
  for (const r of rows) {
    lines.push(
      csvLine([scenarioLabel, r.cohortLabel, r.lens, r.reportingYear, r.ghgCategory, r.kgCo2e, r.basisTag]),
    );
  }
  return lines.join('');
}

// ---------------------------------------------------------------------------
// 5. File 2 — human pivot sheet: reporting-year rows x category columns,
//    org target as its own labeled series (spec §8/§4: "ESG export carries
//    the organization target only").
// ---------------------------------------------------------------------------

/**
 * Pivot rows show the SCENARIO lens (the forecast AFTER every cohort's
 * planned action) summed across every cohort per category per year — the
 * baseline (counterfactual "do nothing") lens is still available in the
 * tidy file for a reader who wants the comparison; the human pivot answers
 * "what will our reported footprint look like," which is the scenario
 * lens. INTERPRETED: the spec does not pin which lens the pivot renders —
 * this choice is documented here so a reviewer can override it.
 */
export interface EsgPivotYearRow {
  reportingYear: number;
  categories: CategoryKgCo2ePoint;
}

/** @pure */
export function buildEsgPivotRows(
  cohorts: readonly CarbonScenarioCohort[],
  horizonMonths: number,
  monthsPerYear = 12,
): EsgPivotYearRow[] {
  const series = buildCarbonScenarioSeries({ horizonMonths, cohorts: [...cohorts] });
  const years = aggregateCarbonSeriesByReportingYear(series, monthsPerYear);
  return years.map((y) => ({ reportingYear: y.reportingYear, categories: y.scenario }));
}

export const ESG_PIVOT_CSV_COLUMNS = [
  'reporting_year',
  'scope2',
  'cat8',
  'cat1_or_2',
  'cat5',
  'cat4',
  'org_target_kgco2e',
] as const;

/**
 * `orgTargetTco2ePerYr` — the org headline `carbon_target.tco2e_per_yr`
 * (null when the org has no target set). Converted to kgCO2e (x1000) to
 * share units with every other pivot column. Rendered as a flat value on
 * EVERY reporting-year row (spec §4: "Absolute-only flat annual budget...
 * drawn flat across the horizon"), never pro-rated or staged.
 *
 * @pure
 */
export function buildEsgPivotCsv(
  rows: readonly EsgPivotYearRow[],
  orgTargetTco2ePerYr: number | null,
): string {
  const targetKgco2e = orgTargetTco2ePerYr == null ? '' : round3(orgTargetTco2ePerYr * 1000);
  const lines: string[] = [esgFramingLineRow(), ESG_PIVOT_CSV_COLUMNS.join(',') + '\n'];
  for (const r of rows) {
    lines.push(
      csvLine([
        r.reportingYear,
        r.categories.scope2,
        r.categories.cat8,
        r.categories.cat1_or_2,
        r.categories.cat5,
        r.categories.cat4,
        targetKgco2e,
      ]),
    );
  }
  return lines.join('');
}

// ---------------------------------------------------------------------------
// 6. File 3 — methodology & assumptions sheet (§14, EVERY item).
// ---------------------------------------------------------------------------

export interface EsgMethodologyParams {
  scenarioLabel: string;
  horizonMonths: number;
  /** From `describeCarbonKernelBasis` (scenario-carbon-kernel.ts) — never
   * re-derived here. */
  basis: CarbonKernelBasis;
  /** The org headline target ONLY (spec: "ESG export carries the
   * organization target only") — null when the org has none set. */
  orgTarget: CarbonTargetDto | null;
  /** Cohort labels whose `refresh` new-fleet leg is NOT modeled (see the API
   * route's module header — no picker-resolved SKU config exists yet for a
   * scenario's replacement fleet). Disclosed by name, mirroring
   * `apps/api/src/lib/scenario-compare.ts`'s existing
   * `hasUnmodeledPostRefreshRemainder` gap-disclosure precedent, rather than
   * silently modeling only the old leg with no note. */
  unmodeledGapCohortLabels?: readonly string[];
}

/**
 * Auto-generates the methodology sheet as a two-column (label, value) CSV
 * with section-header rows (single-cell) — every §14 item, in the order
 * §14 lists them: scenario identity/horizon; verification caveat; per-cohort
 * rate source + grid factor source/version; config scalars + overridden
 * flag; GWP source; data-quality statement per input class; category
 * assignment per flow (incl. Cat-8 convention); timing convention; target
 * metadata; exclusion list with justification; biogenic n/a; global framing.
 *
 * @pure
 */
export function buildEsgMethodologyCsv(params: EsgMethodologyParams): string {
  const { scenarioLabel, horizonMonths, basis, orgTarget, unmodeledGapCohortLabels } = params;
  const lines: string[] = [esgFramingLineRow()];
  const section = (title: string) => lines.push(csvLine([title]));
  const row = (label: string, value: string | number | null) => lines.push(csvLine([label, value]));

  section('Scenario identity + horizon');
  row('scenario', scenarioLabel);
  row('horizon_months', horizonMonths);

  section('Verification caveat');
  row('caveat', ESG_VERIFICATION_CAVEAT);

  section('Location-based method');
  row('badge', ESG_LOCATION_BASED_METHOD_BADGE);
  row('note', ESG_LOCATION_BASED_METHOD_NOTE);

  section('Per-cohort rate source');
  for (const c of basis.cohorts) {
    row(`${c.cohortLabel} — rate source`, c.rateSource);
    if (c.gridIntensitySource != null) row(`${c.cohortLabel} — grid intensity source`, c.gridIntensitySource);
    if (c.gridIntensitySourceVersion != null) {
      row(`${c.cohortLabel} — grid intensity source version`, c.gridIntensitySourceVersion);
    }
  }

  section('Config scalars (value + overridden flag)');
  for (const s of basis.scalars) {
    row(`${s.name} value`, s.value);
    row(`${s.name} overridden`, s.isOverridden ? 'true' : 'false');
  }
  row('disposal factor basis', ESG_DISPOSAL_ADDER_BASIS_NOTE);

  section('GWP value set/source');
  row('gwp_source', ESG_GWP_SOURCE_NOTE);

  section('Data-quality statement per input class');
  row('measured', ESG_DATA_QUALITY_STATEMENTS.measured);
  row('derived', ESG_DATA_QUALITY_STATEMENTS.derived);
  row('platform_default', ESG_DATA_QUALITY_STATEMENTS.platformDefault);

  section('Category assignment per flow');
  for (const key of GHG_CATEGORY_KEYS) {
    row(key, ESG_CATEGORY_LABELS[key]);
  }
  row('cat1_or_2 helper', ESG_CAT1_OR_2_HELPER);
  row('cat5 timing helper', ESG_CAT5_TIMING_HELPER);
  row('cat8 colocation convention', ESG_CAT8_COLO_CONVENTION_NOTE);
  row('unlocated assets', ESG_UNLOCATED_ASSETS_NOTE);
  row('buy-used embodied convention', ESG_BUY_USED_EMBODIED_CONVENTION);
  row('buy-new embodied', ESG_BUY_NEW_EMBODIED_NOTE);
  row('constant grid intensity caveat', ESG_CONSTANT_GRID_INTENSITY_CAVEAT);

  section('Timing convention');
  row('timing', ESG_TIMING_CONVENTION_NOTE);

  section('Target metadata');
  row('type', orgTarget?.targetType ?? 'absolute_budget');
  row('basis', orgTarget?.basis ?? CARBON_TARGET_BASIS);
  if (orgTarget?.targetType === 'reduction_percent') {
    // SIL-474: a reduction-percent target has no flat tco2e_per_yr number —
    // an explicit, self-describing set of lines replaces it (never a blank
    // 'tco2e_per_yr' cell, which would read as "no data" rather than "a
    // different target shape").
    row('base_year', orgTarget.baseYear ?? '');
    row('base_year_tco2e', orgTarget.baseYearTco2e ?? '');
    row('base_year_tco2e_basis', 'customer-provided');
    row('target_year', orgTarget.targetYear ?? '');
    row('reduction_pct', orgTarget.reductionPct ?? '');
  } else {
    row('tco2e_per_yr', orgTarget?.tco2ePerYr ?? '');
    row('base_year', orgTarget?.baseYear ?? '');
  }
  row('recalc_policy_note', orgTarget?.recalcPolicyNote ?? '');
  row('settings helper', ESG_TARGET_SETTINGS_HELPER);
  row('lens helper', ESG_TARGET_LENS_HELPER);

  section('Exclusions (with justification)');
  for (const ex of ESG_EXCLUSIONS) {
    row(ex.category, ex.justification);
  }

  if (unmodeledGapCohortLabels != null && unmodeledGapCohortLabels.length > 0) {
    section('Unmodeled gaps');
    for (const label of unmodeledGapCohortLabels) {
      row(
        `${label} — post-refresh new fleet`,
        'Not modeled: the replacement SKU has no owned assets and therefore no measured or ' +
          'resolvable use-phase/embodied inputs of its own at this layer. Only the retiring old ' +
          'fleet is modeled for this cohort.',
      );
    }
  }

  section('Biogenic CO2');
  row('biogenic', ESG_BIOGENIC_NOTE);

  section('Global framing');
  row('framing', ESG_FRAMING_LINE);

  return lines.join('');
}
